import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { CalendarDays, X } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DateRangePickerProps {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  placeholder?: string;
  className?: string;
}

function toDate(iso: string): Date | undefined {
  if (!iso) return undefined;
  try { return parseISO(iso); } catch { return undefined; }
}

export function DateRangePicker({
  from, to, onChange, placeholder = 'Pick date range', className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);

  const fromDate = toDate(from);
  const toDate_ = toDate(to);
  const range: DateRange | undefined = fromDate ? { from: fromDate, to: toDate_ } : undefined;

  const hasValue = !!from || !!to;

  let label = placeholder;
  if (fromDate && toDate_) {
    label = `${format(fromDate, 'd MMM')} – ${format(toDate_, 'd MMM yyyy')}`;
  } else if (fromDate) {
    label = `${format(fromDate, 'd MMM yyyy')} – …`;
  }

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            'h-10 justify-start gap-2 text-left font-normal',
            !hasValue && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarDays className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">{label}</span>
          {hasValue && (
            <span
              role="button"
              aria-label="Clear dates"
              onClick={(e) => { e.stopPropagation(); onChange('', ''); }}
              className="ml-1 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={range}
          onSelect={(r) => {
            const f = r?.from ? format(r.from, 'yyyy-MM-dd') : '';
            const t = r?.to ? format(r.to, 'yyyy-MM-dd') : '';
            onChange(f, t);
            if (f && t) setOpen(false);
          }}
          initialFocus
          numberOfMonths={1}
        />
      </PopoverContent>
    </Popover>
  );
}
